// © 2026 KaraokeClubs. All rights reserved.
// Public "add a missing venue" flow. A visitor names a karaoke spot we don't
// list yet; it lands in the admin queue and, once approved, is validated +
// scraped automatically. Renders its own trigger button + modal.

"use client";

import { useState } from "react";
import { t } from "@/lib/i18n";
import type { Locale } from "@/lib/types";

export default function AddVenueModal({ locale }: { locale: Locale }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [address, setAddress] = useState("");
  const [link, setLink] = useState("");
  const [note, setNote] = useState("");
  const [submitter, setSubmitter] = useState("");
  const [status, setStatus] = useState<"idle" | "sending" | "done" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  function reset() {
    setName(""); setAddress(""); setLink(""); setNote(""); setSubmitter("");
    setStatus("idle"); setErrorMsg("");
  }

  async function submit() {
    if (!name.trim()) {
      setStatus("error");
      setErrorMsg(t(locale, "add_venue_need_name"));
      return;
    }
    setStatus("sending");
    setErrorMsg("");
    // Route a single "link" field to website or instagram/facebook heuristically.
    const l = link.trim();
    const body: Record<string, string> = {
      name: name.trim(),
      address: address.trim(),
      note: note.trim(),
      submitter: submitter.trim(),
    };
    if (l) {
      if (/instagram\.com/i.test(l)) body.instagram = l;
      else if (/facebook\.com|fb\.com/i.test(l)) body.facebook = l;
      else body.website = l;
    }
    try {
      const res = await fetch("/api/add-venue", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(String(res.status));
      setStatus("done");
    } catch {
      setStatus("error");
      setErrorMsg(t(locale, "add_venue_error"));
    }
  }

  return (
    <>
      <div className="flex justify-center py-8">
        <button
          onClick={() => { reset(); setOpen(true); }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-[13px] font-semibold
                     text-violet-300 bg-violet-500/10 border border-violet-500/20
                     hover:bg-violet-500/15 active:scale-95 transition-all touch-manipulation"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
          {t(locale, "add_venue_cta")}
        </button>
      </div>

      {open && (
        <div
          className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
          onClick={() => setOpen(false)}
        >
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
          <div
            className="relative w-full sm:max-w-md max-h-[88vh] overflow-y-auto
                       bg-zinc-900 border border-white/[0.08] rounded-t-3xl sm:rounded-3xl
                       p-5 sm:p-6 shadow-2xl shadow-black/50"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-center mb-4 sm:hidden">
              <div className="w-10 h-1 rounded-full bg-zinc-700" />
            </div>

            {status === "done" ? (
              <div className="text-center py-8">
                <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-violet-500/15 flex items-center justify-center">
                  <svg className="w-6 h-6 text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-[15px] text-zinc-200 mb-6">{t(locale, "add_venue_thanks")}</p>
                <button
                  onClick={() => setOpen(false)}
                  className="px-6 py-2.5 rounded-xl bg-violet-600 text-white font-semibold text-sm active:scale-95 transition-all"
                >
                  {t(locale, "cancel")}
                </button>
              </div>
            ) : (
              <>
                <h2 className="text-lg font-bold mb-1">{t(locale, "add_venue_title")}</h2>
                <p className="text-[13px] text-zinc-400 mb-5 leading-relaxed">{t(locale, "add_venue_intro")}</p>

                <label className="block text-[12px] font-medium text-zinc-400 mb-1">{t(locale, "add_venue_name")}</label>
                <input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={t(locale, "add_venue_name_ph")}
                  className="w-full mb-3 px-3 py-2.5 rounded-lg bg-zinc-800/80 border border-white/[0.08]
                             text-[14px] text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50"
                />

                <label className="block text-[12px] font-medium text-zinc-400 mb-1">{t(locale, "add_venue_address")}</label>
                <input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder={t(locale, "add_venue_address_ph")}
                  className="w-full mb-3 px-3 py-2 rounded-lg bg-zinc-800/80 border border-white/[0.08]
                             text-[13px] text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50"
                />

                <label className="block text-[12px] font-medium text-zinc-400 mb-1">{t(locale, "add_venue_link")}</label>
                <input
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder={t(locale, "add_venue_link_ph")}
                  className="w-full mb-3 px-3 py-2 rounded-lg bg-zinc-800/80 border border-white/[0.08]
                             text-[13px] text-zinc-100 placeholder:text-zinc-600 focus:outline-none focus:border-violet-500/50"
                />

                <label className="block text-[12px] font-medium text-zinc-400 mb-1">{t(locale, "add_venue_note")}</label>
                <textarea
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                  rows={2}
                  placeholder={t(locale, "add_venue_note_ph")}
                  className="w-full mb-4 px-3 py-2 rounded-lg bg-zinc-800/80 border border-white/[0.08]
                             text-[13px] text-zinc-100 placeholder:text-zinc-600 resize-none focus:outline-none focus:border-violet-500/50"
                />

                {status === "error" && <p className="text-[13px] text-red-400 mb-3">{errorMsg}</p>}

                <div className="flex gap-2">
                  <button
                    onClick={() => setOpen(false)}
                    className="flex-1 py-3 rounded-xl bg-zinc-800 border border-white/[0.06] text-zinc-300 font-semibold text-sm active:scale-[0.98] transition-all"
                  >
                    {t(locale, "cancel")}
                  </button>
                  <button
                    onClick={submit}
                    disabled={status === "sending"}
                    className="flex-1 py-3 rounded-xl bg-gradient-to-r from-violet-600 to-violet-500 text-white font-bold text-sm
                               active:scale-[0.98] transition-all disabled:opacity-60 shadow-lg shadow-violet-900/30"
                  >
                    {status === "sending" ? t(locale, "suggest_sending") : t(locale, "add_venue_submit")}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </>
  );
}
